import { platformFields } from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";
import { renameDevice } from "./client";

/**
 * Tell the server which browser and OS this web app is running on, so the phone
 * can render a real browser mark instead of a generic globe.
 *
 * Mirrors `apps/extension/src/background/platformReport.ts` deliberately: the
 * two surfaces must agree on when a platform is reported, and the sentinel
 * rules they both depend on live in the shared `platformFields` helper.
 *
 * Why it exists: `renameDevice` is otherwise only called at pair time and on a
 * manual rename, so every device paired before the platform fields shipped
 * would never report them and would never get an icon. This is the backfill.
 *
 * Sends at most once per signature change, and swallows failures — cosmetic
 * metadata must never break app start.
 */
export async function reportPlatformIfChanged(): Promise<void> {
  try {
    const ua = typeof navigator !== "undefined" && navigator.userAgent ? navigator.userAgent : "";
    const fields = platformFields(ua);
    // Nothing recognised — send nothing rather than a sentinel, and record no
    // signature, so a later build with a smarter parser still reports.
    if (fields.clientBrowser === undefined && fields.clientOs === undefined) return;

    const signature = `${fields.clientBrowser ?? ""}|${fields.clientOs ?? ""}`;
    const stored = await webStorage.get("deviceId", "token", "selfDeviceName", "platformReported");
    if (stored.platformReported === signature) return;
    if (!stored.deviceId || !stored.token) return;
    // The route requires a valid name alongside the platform fields, so an
    // unnamed device reports on its next rename instead.
    if (!stored.selfDeviceName) return;

    await renameDevice(stored.token, stored.deviceId, stored.selfDeviceName, fields);
    await webStorage.set({ platformReported: signature });
  } catch {
    // See the KDoc: cosmetic metadata must never break app start.
  }
}
